import {
    Bus,
    ShieldCheck,
    Users,
    Building2,
    Home as HomeIcon,
    Flag,
    Mail,
    ArrowRight,
} from "lucide-react";
import InnerHero from "@/components/InnerHero";
import Reveal from "@/components/Reveal";
import { EMAIL } from "@/data";

const HIGHLIGHTS = [
    {
        icon: Bus,
        title: "Unidades cómodas",
        desc: "Vehículos que se destacan por su confort y equipamiento.",
    },
    {
        icon: ShieldCheck,
        title: "Seguridad primero",
        desc: "Choferes profesionales y mantenimiento permanente de la flota.",
    },
];

const TYPES = [
    {
        icon: Users,
        title: "Viajes en grupo",
        desc: "Coordiná la salida de tu grupo con comodidad y una sola coordinación.",
    },
    {
        icon: Building2,
        title: "Empresas",
        desc: "Traslados corporativos y logística de personal en unidades modernas.",
    },
    {
        icon: HomeIcon,
        title: "Familias",
        desc: "Viajá con tu familia con la seguridad y el confort de siempre.",
    },
    {
        icon: Flag,
        title: "Contingentes",
        desc: "Delegaciones, clubes y contingentes grandes con espacio para todos.",
    },
];

export default function Turismo() {
    return (
        <div data-testid="turismo-page">
            <InnerHero
                variant="dark"
                image="/images/micro-blanco.webp"
                crumb="Turismo"
                title="Viajes a medida para tu grupo"
                subtitle="Alquilá nuestros buses para turismo, empresas, viajes en grupo, familias y contingentes."
            />

            {/* INTRO */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid items-center gap-12 lg:grid-cols-2">
                        <Reveal>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                                    Alquiler de buses
                                </p>
                                <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">
                                    Conocé Argentina en grupo
                                </h2>
                                <p className="mt-4 max-w-md text-base text-ink/70">
                                    Si estás pensando en viajar en grupo, con
                                    amigos o en familia, podés contratar nuestro
                                    servicio de alquiler de buses. Contamos con
                                    vehículos que se destacan por su confort y
                                    seguridad.
                                </p>
                                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                                    {HIGHLIGHTS.map((h) => (
                                        <div
                                            key={h.title}
                                            className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm"
                                        >
                                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-light text-brand">
                                                <h.icon className="h-6 w-6" />
                                            </span>
                                            <h3 className="mt-4 text-base font-extrabold uppercase tracking-tight text-ink">
                                                {h.title}
                                            </h3>
                                            <p className="mt-1.5 text-sm text-ink/60">
                                                {h.desc}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.12}>
                            <div className="overflow-hidden rounded-[32px]">
                                <img
                                    src="/images/turismo-puente.webp"
                                    alt="Bus de El Cometa Bis en ruta"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* TYPES */}
            <section className="pb-20 lg:pb-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                            Para cada tipo de viaje
                        </p>
                    </Reveal>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {TYPES.map((t, i) => (
                            <Reveal key={t.title} delay={(i % 4) * 0.08}>
                                <div className="group h-full rounded-3xl border border-black/5 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-brand/10">
                                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                                        <t.icon className="h-7 w-7" />
                                    </span>
                                    <h3 className="mt-5 text-lg font-extrabold uppercase tracking-tight text-ink">
                                        {t.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-ink/60">
                                        {t.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="flex flex-col items-center gap-6 rounded-[36px] bg-brand px-8 py-14 text-center">
                            <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">
                                Contá tu viaje y te ayudamos
                            </h2>
                            <p className="max-w-xl text-base text-white/85">
                                Escribinos con los detalles de tu grupo y
                                coordinamos tu servicio.
                            </p>
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="turismo-contact-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:-translate-y-0.5"
                            >
                                <Mail className="h-4 w-4" />
                                Contactanos
                                <ArrowRight className="h-4 w-4" />
                            </a>
                            <p className="text-sm font-medium text-white/80">
                                {EMAIL}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
